import { Link } from 'react-router-dom';
import { ArrowLeft } from '@phosphor-icons/react';

const PrivacyPolicyPage = () => {
  return (
    <div className="min-h-screen flex flex-col" style={{ fontFamily: "'Arial', sans-serif" }}>
      {/* Purple Header Bar */}
      <header className="bg-[#3D1D72] text-white py-4 px-6 shadow-md">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 hover:opacity-90 transition-opacity">
            <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
              <span className="text-xl font-bold">H</span>
            </div>
            <span className="text-xl font-semibold">HERChain Solutions</span>
          </Link>
          <Link 
            to="/" 
            className="flex items-center gap-2 text-white/80 hover:text-white transition-colors text-sm"
          >
            <ArrowLeft size={18} />
            Back to Home
          </Link>
        </div>
      </header>

      {/* White Body Content */}
      <main className="flex-1 bg-white">
        <div className="max-w-4xl mx-auto px-6 py-12">
          <h1 className="text-3xl md:text-4xl font-bold text-[#3D1D72] mb-2">
            Privacy Policy
          </h1>
          <p className="text-gray-500 mb-8">Effective Date: March 2026</p>
          
          <div className="prose prose-lg max-w-none space-y-8">
            {/* Section 1 */}
            <section>
              <h2 className="text-xl font-bold text-[#3D1D72] mb-3">1. ABOUT HERCHAIN SOLUTIONS</h2>
              <p className="text-gray-700 leading-relaxed">
                HERChain Solutions is a cloud-based procurement management platform built specifically for Caribbean hospitality properties. The platform is operated by HERChain Solutions (pending formal registration as HERChain Solutions LLC) and is accessible at herchainsolutions.com. For all privacy related questions please contact us at <a href="mailto:support@herchainsolutions.com" className="text-[#3D1D72] hover:underline">support@herchainsolutions.com</a>.
              </p>
            </section>

            {/* Section 2 */}
            <section>
              <h2 className="text-xl font-bold text-[#3D1D72] mb-3">2. INFORMATION WE COLLECT</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                We collect account and property information including property name, address, contact details, administrator name, email address, subscription plan, and onboarding configuration.
              </p>
              <p className="text-gray-700 leading-relaxed mb-4">
                We collect user information including names, email addresses, role assignments, login activity, and password data stored exclusively as encrypted hashes using bcrypt.
              </p>
              <p className="text-gray-700 leading-relaxed mb-4">
                We collect procurement data including purchase requisitions, purchase orders, vendor information, freight forwarder information, approval and audit trail records, and receiving confirmations.
              </p>
              <p className="text-gray-700 leading-relaxed mb-4">
                We collect uploaded documents including vendor invoices, Bills of Lading, customs declarations, and all other documents uploaded to the Document Vault.
              </p>
              <p className="text-gray-700 leading-relaxed mb-4">
                We collect inventory data including Item Master List contents, physical inventory count records, and stock level data.
              </p>
              <p className="text-gray-700 leading-relaxed">
                We collect technical information including browser and device information, IP addresses, and platform usage logs.
              </p>
            </section>

            {/* Section 3 */}
            <section>
              <h2 className="text-xl font-bold text-[#3D1D72] mb-3">3. HOW WE USE YOUR INFORMATION</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                We use the information we collect exclusively to provide, operate, and maintain the HERChain Solutions platform for your property, to authenticate users and maintain secure access to role-based portals, to send automatic notifications and reminders as part of the procurement workflow, to generate reports and audit trails within the platform, to provide customer support, to maintain platform security, and to improve the platform using aggregated anonymised data only.
              </p>
              <p className="text-gray-700 leading-relaxed font-medium">
                HERChain Solutions does not sell, rent, trade, or share your personal or business data with any third party for marketing or commercial purposes. Your procurement data belongs to you.
              </p>
            </section>

            {/* Section 4 */}
            <section>
              <h2 className="text-xl font-bold text-[#3D1D72] mb-3">4. DATA OWNERSHIP</h2>
              <p className="text-gray-700 leading-relaxed">
                All data entered into HERChain Solutions by your property belongs entirely to your property. HERChain Solutions does not claim ownership of your business data. Upon termination of your subscription you may request a complete export of your property data within 30 days. After 30 days your data will be permanently deleted from our systems.
              </p>
            </section>

            {/* Section 5 */}
            <section>
              <h2 className="text-xl font-bold text-[#3D1D72] mb-3">5. DATA ISOLATION AND MULTI-TENANT SECURITY</h2>
              <p className="text-gray-700 leading-relaxed">
                Each property's data is stored and accessed in complete isolation from all other properties. No user from one property can access data belonging to another property under any circumstances. All database queries are scoped to the authenticated user's property at the server level.
              </p>
            </section>

            {/* Section 6 */}
            <section>
              <h2 className="text-xl font-bold text-[#3D1D72] mb-3">6. DATA SECURITY</h2>
              <ul className="list-disc list-inside text-gray-700 space-y-2">
                <li>All passwords are encrypted using bcrypt with a cost factor of 12.</li>
                <li>User sessions are authenticated using cryptographically secure JSON Web Tokens with no hardcoded fallback values.</li>
                <li>All data transmitted between users and the platform is encrypted using HTTPS.</li>
                <li>Uploaded documents are stored in a secure cloud file storage service.</li>
                <li>Role-based access controls ensure each user sees only the information relevant to their assigned role.</li>
                <li>API keys and sensitive configuration values are stored as secure environment variables.</li>
              </ul>
            </section>

            {/* Section 7 */}
            <section>
              <h2 className="text-xl font-bold text-[#3D1D72] mb-3">7. DATA RETENTION</h2>
              <ul className="list-disc list-inside text-gray-700 space-y-2">
                <li><strong>Active subscription</strong> — all data retained for the duration of your subscription.</li>
                <li><strong>After subscription termination</strong> — data retained for 30 days to allow for export requests.</li>
                <li><strong>After 30 days</strong> — all property data permanently deleted.</li>
                <li><strong>Technical logs</strong> — retained for 90 days for security monitoring purposes.</li>
              </ul>
            </section>

            {/* Section 8 */}
            <section>
              <h2 className="text-xl font-bold text-[#3D1D72] mb-3">8. THIRD PARTY SERVICES</h2>
              <p className="text-gray-700 leading-relaxed">
                HERChain Solutions uses the following third-party services to operate the platform: Emergent for platform hosting and file storage infrastructure, Resend for email delivery services, and MongoDB for database services. We do not share your data with any third party beyond what is necessary to operate these core platform services.
              </p>
            </section>

            {/* Section 9 */}
            <section>
              <h2 className="text-xl font-bold text-[#3D1D72] mb-3">9. YOUR RIGHTS</h2>
              <p className="text-gray-700 leading-relaxed">
                You have the right to access, correct, export, and request deletion of your data at any time. To exercise any of these rights please contact us at <a href="mailto:support@herchainsolutions.com" className="text-[#3D1D72] hover:underline">support@herchainsolutions.com</a>.
              </p>
            </section>

            {/* Section 10 */}
            <section>
              <h2 className="text-xl font-bold text-[#3D1D72] mb-3">10. COOKIES</h2>
              <p className="text-gray-700 leading-relaxed">
                HERChain Solutions uses essential cookies and session tokens to maintain secure user authentication and platform functionality. We do not use cookies for advertising, tracking, or marketing purposes.
              </p>
            </section>

            {/* Section 11 */}
            <section>
              <h2 className="text-xl font-bold text-[#3D1D72] mb-3">11. CHANGES TO THIS PRIVACY POLICY</h2>
              <p className="text-gray-700 leading-relaxed">
                We may update this Privacy Policy from time to time. When we make changes we will update the effective date and notify active subscribers by email.
              </p>
            </section>

            {/* Section 12 */}
            <section>
              <h2 className="text-xl font-bold text-[#3D1D72] mb-3">12. CONTACT US</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                HERChain Solutions<br />
                <a href="mailto:support@herchainsolutions.com" className="text-[#3D1D72] hover:underline">support@herchainsolutions.com</a><br />
                <a href="https://herchainsolutions.com" className="text-[#3D1D72] hover:underline">herchainsolutions.com</a>
              </p>
              <p className="text-gray-500 text-sm italic">
                Note: HERChain Solutions is currently pending formal registration as HERChain Solutions LLC. This policy will be updated upon completion of registration.
              </p>
            </section>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-[#3D1D72] text-white py-8 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-white/50 text-sm">
              &copy; {new Date().getFullYear()} HERChain Solutions. All rights reserved.
            </p>
            <div className="flex items-center gap-6">
              <span className="text-white text-sm font-medium">Privacy Policy</span>
              <Link 
                to="/terms-of-service" 
                className="text-white/60 hover:text-white text-sm transition-colors"
              >
                Terms of Service
              </Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPolicyPage;
